import type { AxiosResponse } from "axios";
import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export interface MappingFilter {
  id: string;
  uniqueId?: string;
  fields: Record<string, unknown>;
}

export interface DeviceMappingRecord {
  id: string | number;
  mappingTypeId: string;
  packetCode: string;
  realTime?: boolean;
  forwardTo?: string;
  filter?: MappingFilter[];
  filters?: MappingFilter[];
  mappingFields?: Record<string, unknown>;
}

export interface DeviceMappingsResponse {
  id?: string | number;
  linkedMappings?: DeviceMappingRecord[];
}

export interface IncomingValueItem {
  value?: unknown;
  lastUpdate?: string;
  receivedAt?: string;
}

export interface IncomingValueEntry {
  packet_code?: string;
  data?: IncomingValueItem[];
  [key: string]: unknown;
}

export type IncomingValuesResponse = Record<string, Record<string, IncomingValueEntry>>;

export interface MappingPayload {
  unitId?: string | number;
  mappingTypeId: string;
  realTime: boolean;
  forwardTo: string;
  packetCode: string;
  filters?: MappingFilter[];
  mappingFields: Record<string, unknown>;
}

export interface MappingLookupItem {
  id: string | number;
  name?: string;
  code?: string;
}

export const getDeviceIncomingLastValue = (
  params: { unitId?: string | number; brand?: string },
  language?: string
) => {
  const queryParams = new URLSearchParams();

  if (params.unitId) queryParams.append("unitId", String(params.unitId));
  if (params.brand) queryParams.append("brand", params.brand);

  return ApiService.get<{ response: IncomingValuesResponse }>(
    `${ServicesUrlEndpoints.DEVICE_INCOMING_LAST_VALUE}?${queryParams.toString()}`,
    getLanguageHeaders(language)
  );
};

export const getDeviceMappings = (unitId: string | number, language?: string) =>
  ApiService.get<{ response: DeviceMappingsResponse }>(
    `${ServicesUrlEndpoints.DEVICE_MAPPINGS.LISTING}/${unitId}`,
    getLanguageHeaders(language)
  );

export const addDeviceMapping = (payload: MappingPayload, language?: string) =>
  ApiService.post(ServicesUrlEndpoints.DEVICE_MAPPINGS.ADD, payload, getLanguageHeaders(language));

export const updateDeviceMapping = (
  params: {
    id: string | number;
    unitId: string | number;
    payload: Omit<MappingPayload, "unitId">;
  },
  language?: string
) =>
  ApiService.patch(
    `${ServicesUrlEndpoints.DEVICE_MAPPINGS.UPDATE}/${params.id}?unitId=${params.unitId}`,
    params.payload,
    getLanguageHeaders(language)
  );

export const deleteDeviceMapping = (
  params: { id: string | number; unitId: string | number },
  language?: string
) =>
  ApiService.delete(
    `${ServicesUrlEndpoints.DEVICE_MAPPINGS.DELETE}?id=${params.id}&unitId=${params.unitId}`,
    getLanguageHeaders(language)
  );

const getLookupResponse = (
  url: string,
  language?: string
): Promise<AxiosResponse<{ response: MappingLookupItem[] }>> =>
  ApiService.get<{ response: MappingLookupItem[] }>(url, getLanguageHeaders(language));

export const getRelayStatusLookups = (language?: string) =>
  getLookupResponse(ServicesUrlEndpoints.MAPPING_LOOKUP.RELAY_STATUS_TYPE, language);

export const getSwitchOnOffStateTypeLookups = (language?: string) =>
  getLookupResponse(ServicesUrlEndpoints.MAPPING_LOOKUP.SWITCH_ON_OFF_STATE_TYPE, language);

export const getTemperatureTypeLookups = (language?: string) =>
  getLookupResponse(ServicesUrlEndpoints.MAPPING_LOOKUP.TEMPERATURE_TYPE, language);
